import { useEffect, useState } from "react";
import "./devIllustrationLoop.css";

// Looping isometric "dev workspace" illustration — ported from a Claude
// Design prototype (dev-loop.jsx) that used a bespoke scene-animation
// engine. The continuous motion (float, sway, spin, steam) is reproduced
// here with plain CSS keyframes instead, and only the typing/cursor effect
// — which needs discrete state — runs off a lightweight interval, so this
// stays a plain always-on decorative loop with no dependency on that
// engine or its editor tooling.

const C = {
  emerald: "#1772B8", mint: "#3D97DD", pale: "#BFE0F5", deep: "#0B3D63",
  gold: "#4A9FD8", goldL: "#8CC6ED", goldD: "#2C6690", navy: "#08213A",
  screenFrame: "#16233D", screenBg: "#08213A", screenEdge: "#0C1B33",
  keys: "#3E5A70", cream: "#F2F6FA", creamD: "#D9E6F0", brown: "#7A5A34",
};

const CODE_LINES = [
  { x: 284, y: 136, w: 32, c: C.mint },
  { x: 284, y: 146, w: 46, c: C.goldL },
  { x: 284, y: 156, w: 26, c: C.pale },
  { x: 290, y: 166, w: 38, c: C.mint },
  { x: 284, y: 176, w: 20, c: C.goldL },
];

const clamp01 = (v) => Math.max(0, Math.min(1, v));

function TreeLayer({ x, baseY, halfW, h, fill }) {
  return (
    <g>
      <polygon points={`${x},${baseY - h} ${x - halfW},${baseY} ${x},${baseY}`} fill={fill} />
      <polygon points={`${x},${baseY - h} ${x},${baseY} ${x + halfW},${baseY}`} fill={fill} />
      <polygon points={`${x},${baseY - h} ${x},${baseY} ${x + halfW},${baseY}`} fill="#000" opacity="0.22" />
    </g>
  );
}

function Tree({ x, y, className }) {
  return (
    <g className={className}>
      <rect x={x - 10} y={y - 2} width={20} height={47} rx={2} fill={C.goldD} />
      <rect x={x - 10} y={y - 2} width={10} height={47} fill={C.gold} />
      <TreeLayer x={x} baseY={y} halfW={46} h={57} fill={C.deep} />
      <TreeLayer x={x} baseY={y - 41} halfW={38} h={52} fill={C.emerald} />
      <TreeLayer x={x} baseY={y - 82} halfW={30} h={47} fill={C.mint} />
    </g>
  );
}

// Positioning (the SVG `transform` attribute) and animation (a CSS
// `transform` on its own element) must live on different nodes — a CSS
// transform on an element that also carries a transform attribute replaces
// it outright instead of composing, which snaps the element back to the
// canvas origin. Each concern gets its own <g>.
function Diamond({ cx, cy, size, fill, bobClass, spinClass }) {
  return (
    <g className={bobClass}>
      <g transform={`translate(${cx} ${cy})`}>
        <g className={spinClass}>
          <rect x={-size / 2} y={-size / 2} width={size} height={size} fill={fill} opacity="0.85" />
        </g>
      </g>
    </g>
  );
}

// Typing cycle: 5 code lines type in, hold, fade out, repeat every 4s.
function useTypingCycle(active) {
  const [cycle, setCycle] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const start = performance.now();
    const id = setInterval(() => {
      const elapsed = performance.now() - start;
      setCycle((elapsed % 4000) / 4000);
    }, 90);
    return () => clearInterval(id);
  }, [active]);
  return cycle;
}

const DevIllustrationLoop = () => {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const onChange = (e) => setReducedMotion(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const c = useTypingCycle(!reducedMotion);
  const fadeOut = reducedMotion ? 1 : (c < 0.78 ? 1 : clamp01(1 - (c - 0.78) / 0.14));
  const lineVis = CODE_LINES.map((_, i) =>
    reducedMotion ? 1 : clamp01((c - 0.06 - i * 0.11) / 0.06) * fadeOut
  );
  const typedCount = lineVis.filter((v) => v > 0).length;
  const cursorLine = CODE_LINES[Math.min(typedCount, CODE_LINES.length - 1)];
  const blinkOn = reducedMotion ? false : Math.floor(performance.now() / 500) % 2 === 0;

  // Reduced-motion users still get the full illustration — the CSS
  // keyframes freeze themselves via the prefers-reduced-motion media query
  // in devIllustrationLoop.css, and lineVis/blinkOn above already resolve
  // to a settled fully-typed frame instead of animating.
  return (
    <svg
      viewBox="0 0 800 450"
      className="dil-scene"
      role="img"
      aria-labelledby="dil-title"
    >
      <title id="dil-title">Animated developer workspace illustration</title>

      {/* ambient color fields */}
      <ellipse cx={190} cy={120} rx={140} ry={95} fill={C.emerald} opacity={0.07} className="dil-glow" />
      <ellipse cx={640} cy={340} rx={150} ry={100} fill={C.gold} opacity={0.08} className="dil-glow" />
      <ellipse cx={660} cy={110} rx={115} ry={80} fill={C.navy} opacity={0.07} className="dil-glow" />

      {/* floating accent diamonds */}
      <Diamond cx={140} cy={160} size={12} fill={C.gold} bobClass="dil-bob" spinClass="dil-spin" />
      <Diamond cx={668} cy={96} size={9} fill={C.mint} bobClass="dil-bob dil-bob-2" spinClass="dil-spin dil-spin-rev" />
      <Diamond cx={722} cy={300} size={11} fill={C.emerald} bobClass="dil-bob dil-bob-3" spinClass="dil-spin" />

      {/* trees */}
      <ellipse cx={120} cy={428} rx={62} ry={10} fill={C.deep} opacity="0.10" />
      <ellipse cx={680} cy={428} rx={62} ry={10} fill={C.deep} opacity="0.10" />
      <Tree x={120} y={380} className="dil-sway" />
      <Tree x={680} y={380} className="dil-sway dil-sway-r" />

      {/* ground shadow */}
      <ellipse cx={400} cy={410} rx={195} ry={25} fill={C.deep} opacity={0.13} />

      {/* floating platform + everything on it */}
      <g className="dil-float">
        <polygon points="400,182 570,260 400,338 230,260" fill={C.pale} />
        <polygon points="230,260 400,338 400,392 230,314" fill={C.emerald} />
        <polygon points="400,338 570,260 570,314 400,392" fill={C.deep} />
        <polygon points="400,182 570,260 400,338 230,260" fill="#fff" opacity="0.10" />

        <g transform="translate(60 11)">
          {/* laptop screen */}
          <polygon points="268,226 340,190 340,98 268,134" fill={C.screenFrame} />
          <polygon points="274,222 338,189 338,106 274,139" fill={C.screenBg} />
          <polygon points="274,222 338,189 338,106 274,139" fill={C.mint} opacity={0.07} />
          {/* skewed to lie on the screen plane (slope -33/64 ≈ -27.27°) */}
          <g transform="translate(274 0) skewY(-27.27) translate(-274 0)">
            {CODE_LINES.map((L, i) => (
              <rect key={i} x={L.x} y={L.y} width={Math.max(0.01, L.w * lineVis[i])}
                height={4} rx={1} fill={L.c} />
            ))}
            <rect x={cursorLine.x + cursorLine.w * (lineVis[Math.min(typedCount, 4)] || 0) + 2}
              y={cursorLine.y} width={7} height={4} fill={C.mint} opacity={blinkOn ? fadeOut : 0} />
          </g>

          {/* laptop base */}
          <polygon points="340,190 412,226 340,262 268,226" fill={C.screenFrame} />
          <polygon points="268,226 340,262 340,270 268,234" fill={C.screenBg} />
          <polygon points="340,262 412,226 412,234 340,270" fill={C.screenEdge} />
          <polygon points="340,197 398,226 340,255 282,226" fill={C.keys} opacity="0.55" />

          {/* plant */}
          <g transform="translate(290,258)">
            <ellipse cx={0} cy={-16} rx={8} ry={20} fill={C.deep} transform="rotate(-22)" />
            <ellipse cx={0} cy={-16} rx={8} ry={20} fill={C.emerald} />
            <ellipse cx={0} cy={-16} rx={8} ry={20} fill={C.deep} transform="rotate(22)" />
          </g>
          <polygon points="270,273 310,273 304,303 276,303" fill={C.gold} />
          <polygon points="290,273 310,273 304,303 290,303" fill="#000" opacity="0.15" />
          <rect x={267} y={265} width={46} height={10} rx={2} fill={C.goldD} />

          {/* coffee + steam */}
          <g className="dil-steam">
            <path d="M372,252 C368,242 378,237 374,224" stroke={C.pale} strokeWidth="2.5" fill="none" strokeLinecap="round" />
          </g>
          <g className="dil-steam dil-steam-2">
            <path d="M392,252 C388,242 398,237 394,224" stroke={C.pale} strokeWidth="2.5" fill="none" strokeLinecap="round" />
          </g>
          <path d="M406,264 C419,264 419,285 406,285" stroke={C.creamD} strokeWidth="5" fill="none" strokeLinecap="round" />
          <rect x={362} y={257} width={42} height={36} rx={6} fill={C.cream} stroke={C.creamD} strokeWidth="1.5" />
          <rect x={362} y={278} width={42} height={6} fill={C.emerald} />
          <ellipse cx={383} cy={259} rx={19} ry={6} fill={C.brown} />
        </g>
      </g>

      {/* floating code bracket */}
      <g transform="translate(400 76)">
        <g className="dil-bracket">
          <ellipse cx={0} cy={-6} rx={82} ry={38} fill={C.goldL} opacity={0.15} />
          <text x={2.5} y={16} textAnchor="middle" fontFamily="'Courier New', Consolas, monospace"
            fontWeight="700" fontSize="46" fill="#000" opacity="0.18">&lt;/&gt;</text>
          <text x={0} y={14} textAnchor="middle" fontFamily="'Courier New', Consolas, monospace"
            fontWeight="700" fontSize="46" fill={C.gold}>&lt;/&gt;</text>
        </g>
      </g>
    </svg>
  );
};

export default DevIllustrationLoop;
